import {useCallback, useEffect, useRef, useState} from 'react';
import {MatrixEventEvent, RoomEvent} from 'matrix-js-sdk';
import type {MatrixEvent, Room} from 'matrix-js-sdk';
import {matrixService} from '../../services/matrixService';
import {getBotUserId} from '../../utils/aiBotUtils';
import {isTranscriptionMessageContent} from '../../utilis/chatUtils';
import {
    buildAiRequest, isAiTerminal, latestAiStatus, newAiRequestMetadata,
    readLoadedAiRequest, record, requestReference,
} from '../../utils/aiRequestProtocol';
import type {AiInput, AiRequestContent, AiRequestKind, AiStatus} from '../../utils/aiRequestProtocol';

interface Selection {
    readonly roomId: string;
    readonly requesterId: string;
    readonly input: AiInput;
    readonly content: AiRequestContent;
    readonly eventId?: string;
}
interface Progress {
    readonly requestId: string;
    readonly status?: AiStatus;
    readonly resultReceived: boolean;
}
const STATUS_REFRESH_DELAYS_MS = [10_000, 30_000, 60_000, 120_000];
const STATUS_POLLING_WINDOW_MS = 25 * 60_000;
const STATUS_POLLING_EXPIRED = 'This is taking longer than expected. Your request is retained; check its status again.';

export const useAiRequest = ({roomId, currentUserId, kind, audioEventId, enabled}: {
    readonly roomId: string;
    readonly currentUserId: string;
    readonly kind: AiRequestKind;
    readonly audioEventId?: string;
    readonly enabled: boolean;
}) => {
    const [selection, setSelection] = useState<Selection>();
    const [progress, setProgress] = useState<Progress>();
    const [transportError, setTransportError] = useState<string>();
    const [pollingExpiredFor, setPollingExpiredFor] = useState<string>();
    const [sending, setSending] = useState(false);
    const sendingRef = useRef(false);
    const request = selection?.roomId === roomId && selection.requesterId === currentUserId && selection.input.kind === kind
        && (kind !== 'transcription' || (selection.input.kind === 'transcription' && selection.input.audioEventId === audioEventId))
        ? selection : undefined;
    const requestId = request?.content['org.phonehq.ai_request'].request_id;
    const currentProgress = progress?.requestId === requestId ? progress : undefined;
    const status = currentProgress?.status;
    const resultReceived = currentProgress?.resultReceived ?? false;
    const pending = Boolean(request && !resultReceived && (!status || !isAiTerminal(status.state)));

    const scan = useCallback(() => {
        if (!enabled || !currentUserId) return;
        const events = matrixService.getClient()?.getRoom(roomId)?.getLiveTimeline().getEvents();
        const botUserId = getBotUserId(currentUserId);
        if (!events || !botUserId) return;
        const matching = events.filter(event => event.getSender() === currentUserId)
            .map(readLoadedAiRequest).filter(loaded => loaded && loaded.input.kind === kind
                && (loaded.input.kind !== 'transcription' || loaded.input.audioEventId === audioEventId));
        const loaded = request
            ? matching.find(value => value?.content['org.phonehq.ai_request'].request_id === requestId)
            : matching.at(-1);
        const active = loaded ? {roomId, requesterId: currentUserId, ...loaded} : request;
        if (!active) return;
        if (loaded && (!request || request.eventId !== loaded.eventId)) {
            setSelection({roomId, requesterId: currentUserId, ...loaded});
            setTransportError(undefined);
        }
        if (!active.eventId) return;
        const nextStatus = latestAiStatus({events, botUserId, kind, requestEventId: active.eventId});
        const foundResult = events.some(event => {
            const content = event.getContent();
            return !event.isRedacted() && event.getSender() === botUserId && record(content)
                && requestReference(content) === active.eventId
                && (kind === 'transcription'
                    ? isTranscriptionMessageContent(content)
                    : content.msgtype === 'org.phonehq.summary_result'
                        || (content.msgtype === 'm.text' && Boolean(content['org.phonehq.summary_data'])));
        });
        const activeId = active.content['org.phonehq.ai_request'].request_id;
        setProgress(previous => {
            const old = previous?.requestId === activeId ? previous : undefined;
            const newest = old?.status && (!nextStatus || old.status.revision >= nextStatus.revision)
                ? old.status : nextStatus;
            if (old && newest?.id === old.status?.id && foundResult === old.resultReceived) return previous;
            return {requestId: activeId, status: newest, resultReceived: foundResult};
        });
    }, [enabled, currentUserId, roomId, kind, audioEventId, request, requestId]);

    useEffect(() => {
        scan();
        const client = matrixService.getClient();
        if (!client) return;
        const timeline = (_event: MatrixEvent, room?: Room) => { if (room?.roomId === roomId) scan(); };
        const decrypted = (event: MatrixEvent) => { if (event.getRoomId() === roomId) scan(); };
        const redacted = (_event: MatrixEvent, room: Room) => { if (room.roomId === roomId) scan(); };
        client.on(RoomEvent.Timeline, timeline);
        client.on(MatrixEventEvent.Decrypted, decrypted);
        client.on(RoomEvent.Redaction, redacted);
        return () => {
            client.off(RoomEvent.Timeline, timeline);
            client.off(MatrixEventEvent.Decrypted, decrypted);
            client.off(RoomEvent.Redaction, redacted);
        };
    }, [roomId, scan]);

    const checkStatus = useCallback(async () => {
        if (!enabled || !request?.eventId) return;
        try {
            await matrixService.sendAiStatusRequest(roomId, kind, request.eventId);
            setTransportError(undefined);
        } catch {
            setTransportError('Status is temporarily unavailable. Your existing request is retained.');
        }
    }, [enabled, request?.eventId, roomId, kind]);
    useEffect(() => {
        if (!pending || !enabled || !request?.eventId) return;
        const eventId = request.eventId;
        const sentAt = matrixService.getClient()?.getRoom(roomId)?.getLiveTimeline().getEvents()
            .find(event => event.getId() === eventId)?.getTs() ?? Date.now();
        let attempt = 0;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const schedule = () => {
            const delay = STATUS_REFRESH_DELAYS_MS[Math.min(attempt, STATUS_REFRESH_DELAYS_MS.length - 1)];
            if (Date.now() + delay - sentAt > STATUS_POLLING_WINDOW_MS) {
                setPollingExpiredFor(eventId);
                return;
            }
            timer = setTimeout(() => {
                attempt += 1;
                void checkStatus();
                schedule();
            }, delay);
        };
        schedule();
        return () => clearTimeout(timer);
    }, [pending, enabled, request?.eventId, roomId, checkStatus]);
    const pollingExpired = pending && Boolean(request?.eventId) && pollingExpiredFor === request?.eventId;

    const transmit = useCallback(async (next: Selection) => {
        if (sendingRef.current || !enabled) return;
        sendingRef.current = true;
        setSending(true);
        setSelection(next);
        setTransportError(undefined);
        try {
            const eventId = await matrixService.sendAiRequest(next.roomId, next.content);
            setSelection({...next, eventId});
        } catch {
            setTransportError('Delivery of the request is unconfirmed. Check the same request before starting another.');
        } finally {
            sendingRef.current = false;
            setSending(false);
        }
    }, [enabled]);
    const start = useCallback(async (input: AiInput) => {
        if (pending || sendingRef.current || input.kind !== kind) return;
        await transmit({roomId, requesterId: currentUserId, input, content: buildAiRequest(input, newAiRequestMetadata())});
    }, [pending, roomId, currentUserId, kind, transmit]);
    const retry = useCallback(async () => {
        if (!request?.eventId || !status?.retryAllowed || status.state !== 'failed' || resultReceived) return;
        const original = request.content['org.phonehq.ai_request'];
        const metadata = {...newAiRequestMetadata(original.retry_of ?? request.eventId), timezone: original.timezone};
        await transmit({roomId, requesterId: currentUserId, input: request.input, content: buildAiRequest(request.input, metadata)});
    }, [request, status, resultReceived, roomId, currentUserId, transmit]);
    const recover = useCallback(async () => {
        if (!request) return;
        if (request.eventId) await checkStatus();
        else await transmit(request);
    }, [request, checkStatus, transmit]);

    return {requestEventId: request?.eventId, pending, sending, status, resultReceived,
        transportError: transportError ?? (pollingExpired ? STATUS_POLLING_EXPIRED : undefined), start, retry, recover};
};
